import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Target } from "lucide-react";
import { SectionCard } from "@/components/common/SectionCard";
import { EmptyState } from "@/components/common/EmptyState";
import { EntryModal } from "@/components/common/EntryModal";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { api } from "@/lib/api";

const FIELDS = [
  { name: "current_focus", label: "Current focus", type: "textarea", placeholder: "What are you focused on right now?" },
  { name: "active_tasks", label: "Active tasks summary", type: "textarea", placeholder: "Key tasks in flight" },
  { name: "upcoming_deadline", label: "Upcoming deadline", type: "date" },
  { name: "current_blocker", label: "Current blocker", type: "textarea", placeholder: "Anything blocking you?" },
  { name: "latest_update", label: "Latest update", type: "textarea", placeholder: "Most recent note" },
];

const FOUNDER_ACCENT = { Steven: "#1D4ED8", Richard: "#0EA5A4" };

const Field = ({ label, value }) => (
  <div>
    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#5A6B8C]">{label}</p>
    <p className="mt-0.5 whitespace-pre-wrap text-sm text-[#14245F]">{value?.trim() ? value : <span className="text-[#9DAAC6]">Not set</span>}</p>
  </div>
);

export const FounderFocusPanel = () => {
  const [records, setRecords] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => { try { setRecords(await api.founderFocus()); } catch { setRecords([]); } };
  useEffect(() => { load(); }, []);

  const openEdit = (rec) => { setEditing(rec); setModalOpen(true); };
  const handleSave = async (values) => {
    await api.updateFounderFocus(values.founder, values);
    toast.success(`${values.founder}'s focus updated`);
    await load();
  };

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      {records.length === 0 && (
        <div className="md:col-span-2">
          <EmptyState icon={Target} title="Loading founder focus…" description="Steven and Richard's focus records will appear here." />
        </div>
      )}
      {records.map((rec) => {
        const c = FOUNDER_ACCENT[rec.founder] || "#1D4ED8";
        return (
        <SectionCard
          key={rec.founder}
          testid={`focus-card-${rec.founder.toLowerCase()}`}
          accent={c}
          eyebrow="Founder Mission"
          title={`${rec.founder}'s Focus`}
          action={
            <Button size="sm" variant="outline" data-testid={`edit-focus-${rec.founder.toLowerCase()}`}
              onClick={() => openEdit(rec)} className="rounded-full border-[#E4E9F3]">
              <Pencil size={14} /> Edit
            </Button>
          }
        >
          <div className="mb-4 flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarFallback className="font-bold text-white" style={{ backgroundColor: c }}>{rec.founder[0]}</AvatarFallback>
            </Avatar>
            <div>
              <p className="font-heading font-semibold text-[#14245F]">{rec.founder}</p>
              {rec.upcoming_deadline && <p className="text-xs text-[#5A6B8C]">Next deadline: {rec.upcoming_deadline}</p>}
            </div>
          </div>
          <div className="space-y-3">
            <Field label="Current focus" value={rec.current_focus} />
            <Field label="Active tasks" value={rec.active_tasks} />
            <Field label="Current blocker" value={rec.current_blocker} />
            <Field label="Latest update" value={rec.latest_update} />
          </div>
        </SectionCard>
        );
      })}

      <EntryModal open={modalOpen} onOpenChange={setModalOpen}
        title={`Edit ${editing?.founder || ""} Focus`}
        submitLabel="Save Focus"
        fields={FIELDS} initialValues={editing} onSubmit={handleSave} />
    </div>
  );
};
